import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split} from '@dropbox/dig-foundations';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';

const POLL_INTERVAL_MS = 3000;
// Stale task ids report "processing" forever (Celery can't tell unknown from pending).
const MAX_POLL_MS = 30 * 60 * 1000;

const isFinished = (status?: string) => status === 'completed' || status === 'failed';

const formatElapsed = (ms: number) => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

export const TaskStatusLine = ({
  label,
  taskId,
  queuedAt,
  getStatus,
}: {
  label: string;
  taskId: string;
  queuedAt?: number;
  getStatus: (taskId: string) => Promise<{status: string; message: string}>;
}) => {
  const queryClient = useQueryClient();
  const [watchStartedAt] = useState(Date.now);
  const {data, error, dataUpdatedAt} = useQuery({
    queryKey: ['admin_task_status', taskId],
    queryFn: () => getStatus(taskId),
    refetchInterval: (query) =>
      !isFinished(query.state.data?.status) && Date.now() - watchStartedAt < MAX_POLL_MS
        ? POLL_INTERVAL_MS
        : false,
  });

  const finished = isFinished(data?.status);
  useEffect(() => {
    if (finished) {
      queryClient.invalidateQueries({queryKey: ['celery_taskmeta']});
    }
  }, [finished, queryClient]);

  if (error) {
    return (
      <Box role="alert">
        <Text color="error" size="small">
          {error instanceof Error ? error.message : `Failed to check ${label} status.`}
        </Text>
      </Box>
    );
  }

  if (data?.status === 'completed') {
    return (
      <Box role="status">
        <Text size="small">{data.message || `${label} completed.`}</Text>
      </Box>
    );
  }

  if (data?.status === 'failed') {
    return (
      <Box role="alert">
        <Text color="error" size="small">
          {data.message ? `${label} failed: ${data.message}` : `${label} failed.`}
        </Text>
      </Box>
    );
  }

  if (data && dataUpdatedAt - watchStartedAt >= MAX_POLL_MS) {
    return (
      <Box role="status">
        <Text size="small">
          {`${label} still running after ${formatElapsed(MAX_POLL_MS)} (${taskId}). Check the task list below.`}
        </Text>
      </Box>
    );
  }

  const elapsed = data && queuedAt ? ` ${formatElapsed(dataUpdatedAt - queuedAt)}` : '';
  return (
    <Split role="status" alignY="center" gap="8">
      <Split.Item>
        <Spinner size="small" monochromatic />
      </Split.Item>
      <Split.Item>
        <Text size="small">{`${label} running (${taskId})…${elapsed}`}</Text>
      </Split.Item>
    </Split>
  );
};
